import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

// Video testimonianze YouTube Shorts — fissato dal cliente
const TESTIMONIALS_VIDEO_ID = "KwRFAl4YxBI";

// Fallback testimonials — usati per riempire la griglia a 4 quando il DB ne ha meno
const FALLBACK_TESTIMONIALS = [
  {
    id: "fallback-luca",
    kind: "text",
    author: "Luca S.",
    role_it: "Producer & Artista",
    role_en: "Producer & Artist",
    text_it: "Con Perla ho trovato quello che cercavo da anni: gente competente che ci mette la faccia. Rispondono, decidono in fretta e ti fanno sentire un progetto, non un numero.",
    text_en: "With Perla I finally found what I was looking for: real professionals who show up. They answer, they decide fast, and they treat you like a project, not a number.",
  },
];

export default function Testimonials() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const texts = useMemo(() => {
    const fromDb = items.filter((i) => i.kind !== "video");
    if (fromDb.length >= 4) return fromDb;
    const needed = 4 - fromDb.length;
    return [...fromDb, ...FALLBACK_TESTIMONIALS.slice(0, needed)];
  }, [items]);

  return (
    <div data-testid="testimonials-page">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-16 pb-12">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">— Testimonianze</div>
        <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] text-[#0A0A0A]">
          Voci del roster
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[#0A0A0A]/70">
          {lang === "en" ? "The artists we work with, in their own words." : "Gli artisti che gestiamo, con le loro parole."}
        </p>
      </section>

      {/* Featured VERTICAL video + text testimonials preview */}
      <section className="border-y-2 border-[#0A0A0A] bg-[#F2F2EC] py-16" data-testid="testimonials-featured-block">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 grid lg:grid-cols-12 gap-8 items-start">
          {/* Vertical video LEFT (9:16) */}
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F] mb-4">— Video testimonial</div>
            <div className="relative aspect-[9/16] border-2 border-[#0A0A0A] overflow-hidden bg-[#0A0A0A] max-h-[600px] mx-auto lg:mx-0" data-testid="testimonials-video-featured">
              <iframe
                src={`https://www.youtube.com/embed/${TESTIMONIALS_VIDEO_ID}?rel=0`}
                title="Testimonianze Perla Music"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="mt-3 text-xs text-[#0A0A0A]/60 uppercase tracking-widest">
              Perla Music · Video
            </div>
          </div>

          {/* Text recensioni RIGHT */}
          <div className="lg:col-span-7" data-testid="testimonials-quotes-block">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F] mb-4">
              — {lang === "en" ? "What they say" : "Le loro parole"}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {texts.map((tq) => (
                <div key={tq.id} className="border-2 border-[#0A0A0A] p-6 bg-[#FAFAF7] flex flex-col" data-testid={`testimonial-featured-${tq.id}`}>
                  <div className="text-5xl font-display text-[#FF5A1F] leading-none">"</div>
                  <p className="mt-3 text-sm leading-relaxed text-[#0A0A0A]/85 flex-1">
                    {tq[`text_${lang}`] || tq.text_it}
                  </p>
                  <div className="mt-4 pt-3 border-t border-[#0A0A0A]/20">
                    <div className="font-display text-lg uppercase text-[#0A0A0A]">{tq.author}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">{tq[`role_${lang}`] || tq.role_it}</div>
                  </div>
                </div>
              ))}
              {texts.length === 0 && (
                <div className="col-span-2 border-2 border-dashed border-[#0A0A0A]/30 p-8 text-center text-[#0A0A0A]/50 text-sm">
                  {lang === "en" ? "Reviews coming soon — add them via CMS." : "Recensioni in arrivo — aggiungile dal pannello admin."}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
